import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  FaStore, FaStar, FaTags, FaPercent, FaCalendarAlt,
  FaFilter, FaSearch, FaSortAmountDown, FaChevronLeft,
  FaChevronRight, FaEye, FaShoppingCart, FaCubes, FaLayerGroup, FaExclamationCircle, FaRegClock, FaTimes, FaSpinner, FaPlus, FaMinus
} from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import './Marketplace.css';
import './UI/SearchInput.css';

// --- Reservation Modal Component (defined within Marketplace.js) ---
const ReservationModal = ({ product, isOpen, onClose, onReserve }) => {
  const { t } = useTranslation();
  const [variantQuantities, setVariantQuantities] = useState({});
  const [isReserving, setIsReserving] = useState(false);
  const [reservationError, setReservationError] = useState(null);

  // Reset state when modal opens or product changes
  useEffect(() => {
    if (isOpen && product) {
      // Initialize quantities to 0 or {}
      setVariantQuantities({});
      setReservationError(null); // Clear previous errors
    }
  }, [isOpen, product]);

  if (!isOpen || !product) return null;

  const handleQuantityChange = (variantId, change) => {
    setVariantQuantities(prev => {
      const currentQuantity = prev[variantId] || 0;
      const newQuantity = Math.max(0, currentQuantity + change); // Ensure quantity doesn't go below 0
      
      // Optional: Check against available inventory if needed here
      // const variant = product.variants.find(v => v.id === variantId);
      // if (variant && newQuantity > variant.marketplace_inventory) {
      //   return prev; // Or clamp to max available
      // }

      return { ...prev, [variantId]: newQuantity };
    });
     setReservationError(null); // Clear error when quantity changes
  };

  const handleReserveClick = async () => {
    setIsReserving(true);
    setReservationError(null);

    const variantsToReserve = Object.entries(variantQuantities)
      .filter(([_, quantity]) => quantity > 0)
      .map(([variantId, quantity]) => ({
        variant_id: variantId,
        quantity: quantity,
      }));

    if (variantsToReserve.length === 0) {
      setReservationError(t('marketplace.modal.selectQuantityError'));
      setIsReserving(false);
      return;
    }

    const reservationPayload = {
      marketplace_listing_id: product._id,
      variants: variantsToReserve,
    };

    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post('/api/shopify/reserve_marketplace_product', reservationPayload, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      // Call the passed-in onReserve function (e.g., to show success message)
      onReserve(response.data); // Pass created product data if needed
      onClose(); // Close modal on success

    } catch (err) {
      console.error("Reservation failed:", err.response?.data || err.message);
      const errorDetail = err.response?.data?.detail || err.message || t('marketplace.modal.reservationFailed');
      setReservationError(errorDetail);
    } finally {
      setIsReserving(false);
    }
  };

  const getTotalReserved = () => {
    return Object.values(variantQuantities).reduce((sum, qty) => sum + qty, 0);
  };

  return (
    <div className="una-modal-overlay" onClick={onClose}>
      <div className="una-modal-content" onClick={(e) => e.stopPropagation()}>
        <button className="una-modal-close-button" onClick={onClose}>
          <FaTimes />
        </button>
        <h2>{t('marketplace.modal.title')}</h2>
        <div className="una-modal-product-info">
          {product.images && product.images.length > 0 && (
            <img src={product.images[0].url} alt={product.title} className="una-modal-product-image" />
          )}
          <h3>{product.title}</h3>
        </div>

        <div className="una-modal-variants-list">
          <h4>{t('marketplace.modal.selectVariants')}</h4>
          {product.variants && product.variants.length > 0 ? (
            product.variants.map(variant => {
              const reservedQuantity = variantQuantities[variant.id] || 0;
              const available = variant.marketplace_inventory || 0;
              const canIncrease = reservedQuantity < available;
              const canDecrease = reservedQuantity > 0;

              return (
                <div key={variant.id} className="una-modal-variant-item">
                  <div className="una-variant-details">
                    <span className="una-variant-title">{variant.title || variant.sku}</span>
                    <span className="una-variant-stock">
                      {t('marketplace.modal.available')}: {available}
                    </span>
                  </div>
                  <div className="una-quantity-selector">
                    <button
                      onClick={() => handleQuantityChange(variant.id, -1)}
                      disabled={!canDecrease || isReserving}
                      className="una-quantity-button minus"
                    >
                      <FaMinus />
                    </button>
                    <span className="una-quantity-value">{reservedQuantity}</span>
                    <button
                      onClick={() => handleQuantityChange(variant.id, 1)}
                      disabled={!canIncrease || isReserving}
                      className="una-quantity-button plus"
                    >
                      <FaPlus />
                    </button>
                  </div>
                </div>
              );
            })
          ) : (
            <p>{t('marketplace.modal.noVariants')}</p>
          )}
        </div>

        {reservationError && (
          <div className="una-modal-error">
            <FaExclamationCircle /> {reservationError}
          </div>
        )}

        <div className="una-modal-actions">
           <span className="una-modal-total-reserved">
             {t('marketplace.modal.totalReserved')}: {getTotalReserved()}
           </span>
          <button
            className="una-modal-reserve-button"
            onClick={handleReserveClick}
            disabled={isReserving || getTotalReserved() === 0}
          >
            {isReserving ? <FaSpinner className="una-spinner" /> : t('marketplace.modal.reserveButton')}
          </button>
        </div>
      </div>
    </div>
  );
};
// --- End Reservation Modal Component ---

const Marketplace = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalProducts, setTotalProducts] = useState(0);
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isFeaturedOnly, setIsFeaturedOnly] = useState(false);
  
  // Sorting states
  const [sortBy, setSortBy] = useState('published_at');
  const [sortOrder, setSortOrder] = useState('desc');
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [productsPerPage, setProductsPerPage] = useState(10);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProductForReservation, setSelectedProductForReservation] = useState(null);
  const [reservationSuccessMessage, setReservationSuccessMessage] = useState('');

  useEffect(() => {
    fetchProducts();
    // Only fetch categories if they aren't already loaded or if filters change significantly?
    // For simplicity, fetching categories based on current products for now.
    // A dedicated category endpoint would be better.
    if (products.length > 0) {
       deriveCategoriesFromProducts(products);
    } else {
       // Optionally fetch categories even if no products match current filter
       // fetchCategories(); // Uncomment if needed
    }
  }, [selectedCategories, isFeaturedOnly, searchQuery, sortBy, sortOrder, currentPage, productsPerPage]);

  const deriveCategoriesFromProducts = (fetchedProducts) => {
     const allCategories = fetchedProducts
        .flatMap(product => product.categories || [])
        .filter((category, index, self) => self.indexOf(category) === index)
        .sort(); // Sort categories alphabetically
      setCategories(allCategories);
  };

  const fetchProducts = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('access_token');
      
      // Calculate pagination params
      const skip = (currentPage - 1) * productsPerPage;
      const limit = productsPerPage;
      
      const response = await axios.get('/api/shopify/marketplace/products', {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          categories: selectedCategories.length > 0 ? selectedCategories : undefined,
          is_featured: isFeaturedOnly ? true : undefined,
          search_query: searchQuery || undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
          skip,
          limit
        }
      });
      
      setProducts(response.data.products);
      setTotalProducts(response.data.total);
      // Derive categories after fetching products
      deriveCategoriesFromProducts(response.data.products);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCategoryChange = (category) => {
    setSelectedCategories(prev => {
      if (prev.includes(category)) {
        return prev.filter(c => c !== category);
      } else {
        return [...prev, category];
      }
    });
    setCurrentPage(1); // Reset pagination when changing filters
  };

  const handleFeaturedChange = (e) => {
    setIsFeaturedOnly(e.target.checked);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchProducts(); // Trigger fetch on explicit submit
  };

  const handleSortChange = (e) => {
    const [newSortBy, newSortOrder] = e.target.value.split('-');
    setSortBy(newSortBy);
    setSortOrder(newSortOrder);
    setCurrentPage(1);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return t('marketplace.noExpiration');
    try {
       // Format date robustly
       return new Date(dateString).toLocaleDateString(undefined, { // Use user's locale
           year: 'numeric', month: 'short', day: 'numeric'
       });
    } catch (e) {
       console.error("Error formatting date:", dateString, e);
       return t('marketplace.invalidDate');
    }
  };

  // Calculate total pages
  const totalPages = Math.ceil(totalProducts / productsPerPage);
  
  // Generate page numbers array
  const getPageNumbers = () => {
    const pageNumbers = [];
    const MAX_VISIBLE_PAGES = 5;
    
    if (totalPages <= MAX_VISIBLE_PAGES) {
      // Show all pages if there are few
      for (let i = 1; i <= totalPages; i++) {
        pageNumbers.push(i);
      }
    } else {
      // Always show first page
      pageNumbers.push(1);
      
      // Calculate middle range
      let startPage = Math.max(2, currentPage - 1);
      let endPage = Math.min(totalPages - 1, currentPage + 1);
      
      // Adjust range if at edges
      if (currentPage <= 2) {
        endPage = 4;
      } else if (currentPage >= totalPages - 1) {
        startPage = totalPages - 3;
      }
      
      // Add ellipsis if needed
      if (startPage > 2) {
        pageNumbers.push('...');
      }
      
      // Add middle pages
      for (let i = startPage; i <= endPage; i++) {
        pageNumbers.push(i);
      }
      
      // Add ellipsis if needed
      if (endPage < totalPages - 1) {
        pageNumbers.push('...');
      }
      
      // Always show last page
      pageNumbers.push(totalPages);
    }
    
    return pageNumbers;
  };

  // --- Modal Handling Functions ---
  const openReservationModal = (product) => {
    setSelectedProductForReservation(product);
    setIsModalOpen(true);
     setReservationSuccessMessage(''); // Clear previous success message
  };

  const closeReservationModal = () => {
    setIsModalOpen(false);
    setSelectedProductForReservation(null);
  };

  const handleReservationSuccess = (createdProduct) => {
     // Optionally show a success message
     setReservationSuccessMessage(t('marketplace.modal.reservationSuccess', { title: createdProduct.title }));
     // Optionally: Refresh products list or navigate user?
     // fetchProducts(); // Refresh the list if needed
     console.log("Reserved product created:", createdProduct);

      // Clear the success message after a few seconds
      setTimeout(() => setReservationSuccessMessage(''), 5000);
  };
  // --- End Modal Handling ---

  if (error) return <div className="una-error">{t('common.error')}: {error}</div>;

  return (
    <div className="una-marketplace-main-container">
      <div className="una-marketplace-header">
        
        <div className="una-marketplace-header-actions">
          <Link to="/marketplace-listing" className="una-header-button">
            <FaCubes /> {t('marketplace.myListings')}
          </Link>
          <Link to="/my-marketplace" className="una-header-button">
            <FaLayerGroup /> {t('marketplace.myProducts')}
          </Link>
        </div>
      </div>
      
      {/* Success Message Area */}
       {reservationSuccessMessage && (
         <div className="una-success-message">
           {reservationSuccessMessage}
         </div>
       )}

      <div className="una-marketplace-filters-container">
        <div className="una-filter-panel">
          <h3><FaFilter /> {t('marketplace.filters')}</h3>
          
          <div className="una-filter-section">
            <h4><FaTags /> {t('marketplace.categories')}</h4>
            <div className="una-categories-list">
              {categories.length === 0 && !isLoading && <p>{t('marketplace.noCategories')}</p>}
              {categories.map(category => (
                <label key={category} className="una-category-checkbox">
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(category)}
                    onChange={() => handleCategoryChange(category)}
                  />
                  {category}
                </label>
              ))}
            </div>
          </div>
          
          <div className="una-filter-section star-filter">
            <label className="una-star-toggle-label">
              <input
                type="checkbox"
                className="una-star-toggle-checkbox" // Use a real checkbox
                checked={isFeaturedOnly}
                onChange={handleFeaturedChange} // Use the handler directly
                aria-label={t('marketplace.featuredOnly')}
              />
              <FaStar className={`una-star-icon ${isFeaturedOnly ? 'active' : ''}`} />
              {t('marketplace.featuredOnly')}
            </label>
          </div>
        </div>
        
        <div className="una-marketplace-content">
          <div className="una-marketplace-toolbar">
            <form onSubmit={handleSearchSubmit} className="una-search-container">
              <button type="submit" className="una-search-button">
                <FaSearch />
              </button>
              <input
                type="text"
                placeholder={t('marketplace.searchPlaceholder')}
                className="una-search-input"
                value={searchQuery}
                onChange={handleSearchChange}
              />
            </form>
            
            <div className="una-sort-container">
              <label className="una-sort-label">
                <FaSortAmountDown /> {t('marketplace.sortBy')}:
              </label>
              <select 
                className="una-sort-select"
                value={`${sortBy}-${sortOrder}`}
                onChange={handleSortChange}
              >
                <option value="published_at-desc">{t('marketplace.newest')}</option>
                <option value="published_at-asc">{t('marketplace.oldest')}</option>
                <option value="price_min-asc">{t('marketplace.priceLowToHigh')}</option>
                <option value="price_min-desc">{t('marketplace.priceHighToLow')}</option>
                <option value="title-asc">{t('marketplace.titleAZ')}</option>
                <option value="title-desc">{t('marketplace.titleZA')}</option>
              </select>
            </div>
          </div>
          
          {isLoading ? (
            <div className="una-loading-container">
              <div className="una-loading-spinner"></div>
              <div className="una-loading-text">{t('common.loading')}</div>
            </div>
          ) : products.length === 0 ? (
            <div className="una-empty-state">
              <FaExclamationCircle />
              <p>{t('marketplace.noProductsFound')}</p>
            </div>
          ) : (
            <>
              <div className="una-marketplace-table-container">
                <table className="una-marketplace-table">
                  <thead>
                    <tr>
                      <th></th>
                      <th>{t('marketplace.product')}</th>
                      <th>{t('marketplace.price')}</th>
                      <th>{t('marketplace.commission')}</th>
                      <th>{t('marketplace.categories')}</th>
                      <th>{t('marketplace.availableUntil')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.map(product => (
                      <tr
                         key={product._id}
                         onClick={() => openReservationModal(product)}
                         className="una-product-row-clickable" // Add class for styling/cursor
                      >
                        <td className="una-product-image-cell">
                          {product.images && product.images.length > 0 ? (
                            <img 
                              src={product.images[0].url} 
                              alt={product.title} 
                              className="una-product-thumbnail" 
                            />
                          ) : (
                            <div className="una-product-no-image">
                              <FaStore />
                            </div>
                          )}
                          {product.is_featured && (
                            <span className="una-featured-indicator">
                              <FaStar />
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="una-product-title">{product.title}</div>
                          {product.description && (
                            <div className="una-product-description">
                              {typeof product.description === 'string'
                                 ? (product.description.length > 100
                                     ? `${product.description.substring(0, 100)}...`
                                     : product.description)
                                 : t('marketplace.noDescription')
                              }
                            </div>
                          )}
                        </td>
                        <td>
                          {product.price_range && product.price_range.min != null ? (
                            <>
                              {product.price_range.min} {product.price_range.currency}
                              {product.price_range.min !== product.price_range.max && product.price_range.max != null &&
                                ` - ${product.price_range.max} ${product.price_range.currency}`}
                            </>
                          ) : t('marketplace.priceUnavailable')}
                        </td>
                        <td>
                          {product.commission_rate != null ? (
                             <span className="una-commission-rate">
                               <FaPercent /> {product.commission_rate}%
                             </span>
                          ) : '-'}
                        </td>
                        <td>
                          <div className="una-table-categories">
                            {product.categories && product.categories.length > 0
                               ? product.categories.map(category => (
                                   <span key={category} className="una-category-pill">
                                     <FaTags /> {category}
                                   </span>
                                 ))
                               : '-'}
                          </div>
                        </td>
                        <td>
                          {product.available_until ? (
                            <span className="una-expiry-date">
                              <FaRegClock /> {formatDate(product.available_until)}
                            </span>
                          ) : (
                            <span className="una-no-expiry">{t('marketplace.noExpiration')}</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="una-pagination-container">
                <div className="una-pagination-info">
                  {t('marketplace.showing')} {(currentPage - 1) * productsPerPage + 1} - {Math.min(currentPage * productsPerPage, totalProducts)} {t('marketplace.of')} {totalProducts}
                </div>
                
                <div className="una-pagination-controls">
                  <button 
                    className="una-pagination-button" 
                    disabled={currentPage === 1}
                    onClick={() => handlePageChange(currentPage - 1)}
                    aria-label={t('common.previousPage')}
                  >
                    <FaChevronLeft />
                  </button>
                  
                  {getPageNumbers().map((pageNum, index) => (
                    <button 
                      key={index}
                      className={`una-pagination-button ${pageNum === currentPage ? 'active' : ''} ${typeof pageNum !== 'number' ? 'ellipsis' : ''}`}
                      onClick={() => typeof pageNum === 'number' && handlePageChange(pageNum)}
                      disabled={typeof pageNum !== 'number'}
                    >
                      {pageNum}
                    </button>
                  ))}
                  
                  <button 
                    className="una-pagination-button" 
                    disabled={currentPage === totalPages}
                    onClick={() => handlePageChange(currentPage + 1)}
                    aria-label={t('common.nextPage')}
                  >
                    <FaChevronRight />
                  </button>
                </div>
                
                <div className="una-pagination-per-page">
                  <select 
                    value={productsPerPage} 
                    onChange={e => {
                      setProductsPerPage(Number(e.target.value));
                      setCurrentPage(1); // Reset to first page when changing items per page
                    }}
                    aria-label={t('marketplace.itemsPerPage')}
                  >
                    <option value="10">10</option>
                    <option value="25">25</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                  </select>
                  <span>{t('marketplace.perPage')}</span>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Render the modal */}
      <ReservationModal
        product={selectedProductForReservation}
        isOpen={isModalOpen}
        onClose={closeReservationModal}
        onReserve={handleReservationSuccess}
      />
    </div>
  );
};

export default Marketplace;